import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog'
import { catchError, throwError, timer } from 'rxjs'
import { ErrorMsg } from '../model/selfservice.model'

/**
 * Resolves the error message text or innerHtml based on the error message type.
 */
interface ErrorMsgText {
  text?: string
  innerHtml?: string
  /**
   * Überschrift des Dialogs. Fehlt sie, steht „Fehler" da.
   *
   * Nicht jeder 409 ist eine Störung: „schon unterschrieben" und „aus der Bemusterung ist eine
   * Versorgung geworden" sind abgeschlossene Vorgänge. Unter der Überschrift „Fehler" liest die
   * Patient:in daraus, dass etwas kaputt ist, und ruft im Sanitätshaus an — genau das soll der Text
   * verhindern.
   */
  title?: string
  /** Unterdrückt den Schlusssatz „Melden Sie sich bei Ihrem Leistungserbringer" — s. {@link title}. */
  keinFehler?: boolean
}

export function resolveErrorMessage(payload: ErrorMsg): ErrorMsgText {
  switch (payload.type) {
    case 'UpdateHealthinsuranceInfoAfterVersorgungsanlageError':
      return { text: 'Die Versicherungsdaten können nicht aktualisiert werden, da bereits eine Versorgung angelegt wurde.' }
    case 'PatientMarkedAsDeadError':
      return { text: 'Die Aktion kann nicht ausgeführt werden, da der Patient als verstorben markiert ist.' }
    case 'PatientHasActiveVersorgungenError':
      return { text: 'Der Patient kann nicht gelöscht werden, da bereits mindestens eine Versorgung beliefert wurde.' }
    case 'PflegeboxVersorgungCanNotDelete':
      return {
        text: 'Die Versorgung kann nicht gelöscht werden, da sie bereits in Bearbeitung ist oder noch eine PG51 Versorgung vorliegt.',
      }
    case 'HimiVersorgungCanNotDelete':
      return { text: 'Die Hilfsmittel-Versorgung kann nicht gelöscht werden, da sie bereits in Bearbeitung ist.' }
    case 'PARCEL_WAS_ALREADY_PICKED_UP_FOR_GIVEN_MONTH':
      return { text: 'Für diesen Monat wurde bereits eine Abholung bestätigt.' }
    case 'EKV_NOT_MANUALLY_APPROVED':
      return { text: 'Die Aktion kann nicht ausgeführt werden, da der Kostenvoranschlag noch nicht genehmigt wurde.' }
    case 'EKV_EDIT_BLOCKED_BY_DELIVERY_IN_SHIPPING':
      return { text: 'Die Aktion kann nicht ausgeführt werden, da bereits eine Lieferung im Versand ist.' }
    case 'DELIVERY_INTERVAL_CHANGE_BLOCKED_BY_PER_MONTH_AMOUNTS':
      return {
        innerHtml: `Das Lieferintervall kann hier nicht direkt geändert werden, weil für diese Versorgung Mengen je Monat hinterlegt sind.
        <p>Bitte bearbeiten Sie die Versorgung. Dort lassen sich Lieferintervall und die Mengen je Monat gemeinsam anpassen.</p>`,
      }
    case 'ONLY_SANIUP_USER_ALLOWED':
      return { text: 'Diese Aktion ist nur für SaniUp-Mitarbeiter:innen verfügbar.' }
    // Ein Code je Grund (AttachmentTooLargeException.Grund im Backend), Zahlen wie im
    // DocumentScannerService. Die ausführlichere Fassung für das Haus steht im Interceptor der
    // Leistungserbringer-App.
    case 'ATTACHMENT_TOO_LARGE':
      return { text: 'Die Datei ist größer als 8 MB. Bitte laden Sie ein kleineres Foto oder einen Scan mit geringerer Auflösung hoch.' }
    case 'ATTACHMENT_NICHT_KOMPRIMIERBAR':
      return {
        text: 'Die Datei ist zu groß und ließ sich nicht automatisch verkleinern. Bitte laden Sie ein kleineres Foto oder einen Scan mit geringerer Auflösung hoch.',
      }
    case 'ATTACHMENT_ZU_VIELE_SEITEN':
      return { text: 'Die Datei ist zu groß und hat mehr als 10 Seiten. Bitte laden Sie die Seiten einzeln hoch.' }
    case 'ATTACHMENT_BILD_AUFLOESUNG_ZU_HOCH':
      return { text: 'Das Foto hat eine zu hohe Auflösung. Bitte speichern Sie es in kleinerer Größe und laden Sie es erneut hoch.' }
    case 'PATIENT_EMAIL_REQUIRED_FOR_INVOICE_TRANSMISSION':
      return {
        text: 'Für den Rechnungsversand per E-Mail müssen Sie zunächst eine E-Mail-Adresse hinterlegen. Bitte ergänzen Sie diese unter „Meine Daten ändern“.',
      }
    // Die Signier-Seite (documents/document-sign-page) bedient phm, himi und bemusterung. Ihre
    // Ablehnungen kamen bis 2026-08-14 alle im `default`-Zweig heraus — die Patient:in las
    // „Fehlercode: BEMUSTERUNG_SIGNATURE_NOT_SUPPORTED" (Befund Tim: „ist noch ein bisschen sehr
    // technisch"). Keiner dieser Fälle ist eine Störung, deshalb tragen sie eigene Überschriften.
    case 'BEMUSTERUNG_VERSORGUNG_BEREITS_ANGELEGT':
      return {
        title: 'Nichts mehr zu unterschreiben',
        keinFehler: true,
        innerHtml: `Aus der Bemusterung ist inzwischen eine Versorgung geworden. Dieser Link gilt deshalb nicht mehr.
        <p>Sie müssen nichts tun. Falls noch etwas zu unterschreiben ist, erhalten Sie dafür eine neue Nachricht.</p>`,
      }
    case 'BEMUSTERUNG_ABGEBROCHEN':
      return {
        title: 'Vorgang abgeschlossen',
        keinFehler: true,
        innerHtml: `Diese Bemusterung wurde von Ihrem Sanitätshaus abgebrochen. Dieser Link gilt deshalb nicht mehr.
        <p>Sie müssen nichts tun. Bei Fragen wenden Sie sich bitte an Ihr Sanitätshaus.</p>`,
      }
    case 'BEMUSTERUNG_SIGNATURE_NOT_REQUIRED':
    case 'HIMI_SIGNATURE_NOT_REQUIRED':
    case 'SEPA_VOLLMACHT_NICHT_OFFEN':
      return {
        title: 'Bereits unterschrieben',
        keinFehler: true,
        text: 'Diese Unterlagen sind bereits unterschrieben. Sie müssen nichts weiter tun.',
      }
    // Die beiden Pflegehilfsmittel-Ablehnungen. Getrennt, weil der nächste Schritt ein anderer ist:
    // beim Abgabeweg kann die Patient:in neu wählen, beim Haus ohne Angebot gibt es nichts zu tun.
    // Ohne eigenen Fall stand hier der `default`-Zweig, also „Fehlercode: PFLEGEBOX_NICHT_ANGEBOTEN".
    case 'PFLEGEBOX_NICHT_ANGEBOTEN':
      return {
        title: 'Keine Pflegehilfsmittel im Angebot',
        keinFehler: true,
        text: 'Dieses Sanitätshaus bietet Pflegehilfsmittel zurzeit nicht an. Bei Fragen wenden Sie sich bitte an Ihr Sanitätshaus.',
      }
    case 'ABGABEWEG_NICHT_ANGEBOTEN':
      return {
        text: 'Der gewählte Weg (Versand oder Abholung) steht bei diesem Sanitätshaus nicht mehr zur Verfügung. Bitte laden Sie die Seite neu und wählen Sie erneut.',
      }
    // Bestellung fuer eine Person, die das Haus schon kennt, mit einem anderen Geburtsdatum. Das Backend
    // gibt dann weder ihre Daten heraus noch aendert es sie; der naechste Schritt ist das Geburtsdatum.
    case 'PORTAL_KONTO_NICHT_BESTAETIGT':
      return {
        title: 'Geburtsdatum prüfen',
        keinFehler: true,
        text: 'Das Geburtsdatum passt nicht zu den Angaben, die Ihr Sanitätshaus schon gespeichert hat. Bitte prüfen Sie das Geburtsdatum der versorgten Person. Stimmt es, wenden Sie sich bitte an Ihr Sanitätshaus.',
      }
    // Die Abhol-Seite hinter dem QR-Code (abholung/abholung-quittieren). Dieselben Saetze wie dort im
    // jeweiligen Zustand: der Dialog kommt nur, wenn sich der Zustand zwischen Laden und Absenden
    // geaendert hat, und danach zeigt die neu geladene Seite genau das. Keiner davon ist eine Stoerung.
    case 'ABHOLUNG_BEREITS_BESTAETIGT':
      return {
        title: 'Abholung bestätigt',
        keinFehler: true,
        text: 'Diese Abholung ist bereits bestätigt. Sie müssen nichts weiter tun.',
      }
    case 'ABHOLUNG_NOCH_NICHT_ABHOLBEREIT':
      return {
        title: 'Noch nicht bereit',
        keinFehler: true,
        text: 'Diese Abholung ist noch nicht bereit. Bitte bestätigen Sie sie erst, wenn Sie die Produkte bekommen haben.',
      }
    case 'ABHOLUNG_BELEG_NICHT_MEHR_AKTUELL':
      return {
        title: 'Abholschein nicht mehr aktuell',
        keinFehler: true,
        text: 'Dieser Abholschein ist nicht mehr aktuell. Bitte lassen Sie sich am Tresen einen neuen geben.',
      }
    case 'ABHOLUNG_NICHT_MOEGLICH':
      return {
        title: 'Bestätigung nicht möglich',
        keinFehler: true,
        text: 'Diese Abholung kann hier nicht bestätigt werden. Bitte wenden Sie sich an Ihr Sanitätshaus.',
      }
    case 'ABHOLUNG_DATUM_AUSSERHALB_LIEFERMONAT':
      return {
        title: 'Abholtag prüfen',
        keinFehler: true,
        text: 'Der Abholtag muss im Liefermonat liegen und darf nicht in der Zukunft liegen.',
      }
    case 'BEMUSTERUNG_SIGNATURE_NOT_SUPPORTED':
    case 'HIMI_SIGNATURE_NOT_SUPPORTED':
      return {
        text: 'Diese Unterlagen lassen sich hier nicht digital unterschreiben. Bitte wenden Sie sich an Ihr Sanitätshaus. Die Unterschrift auf Papier ist weiterhin möglich.',
      }
    default:
      return { text: 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut. (Fehlercode: ' + payload.type + ')' }
  }
}

@Component({
  template: `
    <h2 matDialogTitle>{{ msg.title ?? 'Fehler' }}</h2>
    <mat-dialog-content>
      @if (msg.innerHtml) {
        <p [innerHTML]="msg.innerHtml"></p>
      } @else {
        <p>
          {{ msg.text }}
        </p>
      }

      @if (!msg.keinFehler) {
        <p>Melden Sie sich bei Ihrem Leistungserbringer, falls der Fehler weiterhin besteht.</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button mat-dialog-close>Schließen</button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatButtonModule],
})
export class ErrorMsgDialogComponent {
  readonly msg = resolveErrorMessage(inject(MAT_DIALOG_DATA) as ErrorMsg)
}

export const errorMsgInterceptor: HttpInterceptorFn = (req, next) => {
  const dialog = inject(MatDialog)
  return next(req).pipe(
    catchError((err) => {
      if (err instanceof HttpErrorResponse) {
        if (err.status >= 300 && err.headers.get('X-Error') === 'true') {
          const payload: ErrorMsg = err?.error
          timer(0).subscribe(() => dialog.open(ErrorMsgDialogComponent, { data: payload }))
        }
      }
      return throwError(() => err)
    }),
  )
}
